import { Button, Modal, Text } from '@fozito/common-ui';
import { Form } from 'antd';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import ReactSignatureCanvas from 'react-signature-canvas';
import { styled } from 'styled-components';
import ModalSMS, { IModalSMSFuncFinish, useModalSMSRef } from './ModalSMS';
import ModalEmail, { IModalEmailFuncFinish, useModalEmailRef } from './ModalEmail';
type ICBActions = {
  close: () => void;
};

export type ISignatureActionFunc = (signature: string, cbActions: ICBActions) => void;
export type ISMSFuncType = (data: { signature: string, phone: string }, cbActions: ICBActions) => void;
export type IEmailFuncType = (data: { signature: string, email: string }, cbActions: ICBActions) => void;

interface ISignature {
  visible?: boolean;
  onClose?: () => void;
  onAgree: (val: string) => void;
  onNoReceipt?: ISignatureActionFunc;
  onEmail?: IEmailFuncType;
  onSMS?: ISMSFuncType;
  defaultSMSPhone?: string;
  onSignatureChange?: (dataUrl: string) => void;
}
const ratio = 1.5;
const getRatio = (val: number) => ratio * val;
export interface ISignatureRef {
  open: () => void;
  close: () => void;
  setValue: (data: string) => void;
}

const RequestSignature = forwardRef<ISignatureRef, ISignature>(({
  onAgree,
  onNoReceipt,
  onSignatureChange = () => undefined,
  onClose = () => undefined,
  onSMS,
  onEmail,
  defaultSMSPhone,
}, ref) => {
  const [isOpenModal, setIsOpenModal] = useState(false);
  const signRef = useRef<ReactSignatureCanvas>(null);
  const [smsForm] = Form.useForm();
  const [emailForm] = Form.useForm();
  const smsRef = useModalSMSRef();
  const emailRef = useModalEmailRef();

  const handleClear = () => {
    signRef.current?.clear();
    onSignatureChange('');
  };

  const getSignatureValue = () => {
    if (signRef.current?.isEmpty()) return '';
    return signRef.current?.toDataURL();
  };

  const handleAgree = () => {
    const val = getSignatureValue();
    onAgree(val ?? '');
    onClose();
  };

  const onFormSMSFinish: IModalSMSFuncFinish = (values) => {
    const cbActions: ICBActions = {
      close: () => setIsOpenModal(false),
    };
    if (!onSMS) return;
    smsRef.current?.close();
    const val = getSignatureValue();
    onSMS({ signature: val || '', phone: values.phone }, cbActions);
  };

  const onFormEmailFinish: IModalEmailFuncFinish = (values) => {
    const cbActions: ICBActions = {
      close: () => setIsOpenModal(false),
    };
    if (!onEmail) return;
    emailRef.current?.close();
    const val = getSignatureValue();
    onEmail({ signature: val || '', email: values.email }, cbActions);
  };

  const cbActions: ICBActions = useMemo(() => ({
    close: () => setIsOpenModal(false),
  }), [setIsOpenModal]);

  useImperativeHandle(ref, () => ({
    open: () => {
      signRef.current?.clear();
      setIsOpenModal(true);
    },
    close: () => setIsOpenModal(false),
    setValue: (val) => {
      signRef.current?.clear();
      signRef.current?.fromDataURL(val, { height: getRatio(300), width: getRatio(750) });
    },
  }));

  return (
    <>
      <SendEmailModalStyled
        title='CUSTOMER SIGNATURE'
        closable={false}
        visible={isOpenModal}
        onClose={() => undefined}
        width={'auto'}
        className='modal-signature'
      >
        <div className="signature-container">
          <div className="signature-area">
            <ReactSignatureCanvas ref={signRef}
              canvasProps={{
                width: getRatio(750),
                height: getRatio(300),
                style: { backgroundColor: 'transparent' },
                className: 'sigCanvas',
              }}
              onEnd={(event: MouseEvent) => {
                const element = event?.target as HTMLCanvasElement;
                if (element?.toDataURL)
                  onSignatureChange(element?.toDataURL() || '');
              }}
            />
          </div>
          <div style={{ position: 'absolute', bottom: 0, left: 16, right: 16 }}>
            <Text text_left title_h4_24_medium style={{ alignSelf: 'start', borderBottom: '1px solid' }} className='prevent-select' >Sign here X</Text>
            <Text className="signature-description prevent-select">
              Cardholder agrees to pay the card issuer the amount of service
              above, and it can not dispute or refundable.
            </Text>
          </div>
        </div>
        <AcceptButtons>
          <Button type_cancel onClick={handleClear}>Clear</Button>
          {onNoReceipt && <Button type_cancel onClick={() => {
            const val = getSignatureValue();
            onNoReceipt(val || '', cbActions);
          }}>No Receipt</Button>}
          {onEmail && <Button type_tertiary onClick={() => emailRef.current?.open()}>Email</Button>}
          {onSMS && <Button type_orange onClick={() => smsRef.current?.open(defaultSMSPhone || '')}>SMS</Button>}
          <Button type_primary onClick={handleAgree}>Ok</Button>
        </AcceptButtons>
        {onSMS && <ModalSMS ref={smsRef} form={smsForm} onFinish={onFormSMSFinish} />}
        {onEmail && <ModalEmail ref={emailRef} form={emailForm} onFinish={onFormEmailFinish} />}
      </SendEmailModalStyled>
    </>
  );
});
RequestSignature.displayName = 'RequestSignature';
export default RequestSignature;
const AcceptButtons = styled.div`
display: flex;
gap: 16px;
align-self: center;
padding: 16px;
button {
  flex: 1;
}
`;
const SendEmailModalStyled = styled(Modal)`

    .signature-container {
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        position: relative;
        margin: auto;
        margin-bottom: 1.5rem;
    }
    .signature-area {
      flex-shrink: 0;
      border-radius: 0.3125rem;
      position: relative;
      z-index: 2;
      display: flex;
      align-items: center;
      justify-content: end;
    }
    .signature-description {
      color: #64748B;
      text-align: center;
      font-family: Poppins;
      font-size: 16px;
      font-style: italic;
      font-weight: 400;
      line-height: normal;
    }
    .signature-divider {
        width: 100%;
    }
    .ant-modal-footer .pay-modal-group-btn-footer {
      .pay-footer-modal-btn {
        width: 8.5rem;
        flex: unset;
      }
    }
`;
